// =============================================================================
// UserAdminShelterList.jsx — the Shelters list (Manage → Shelters)
// =============================================================================
// Slimmed on 5 July 2026 (shelter-admin tabbed redesign Phase 1): the old
// inline View panel + inline Edit form are GONE — clicking the Shelter Code
// (or Edit) now opens the full-screen tabbed ShelterDetail (/users/:code),
// which owns viewing + editing. This component is a pure list.
// =============================================================================

// Sort + Sales Order Group (13 Oct 2026): every column heading except Actions is a
// click-to-sort button (`sort` = { key, dir }, `onSort(key)` — the parent owns the sort
// and applies it to the WHOLE filtered list before paging). Optional: without `onSort`
// the headings stay plain text. A "Sales Order Group" column (`sales_group`) was added.
const COLUMNS = [
  { key: "code", label: "DFV Service Code" },
  { key: "name", label: "DFV Service Name" },
  { key: "state", label: "State" },
  { key: "type", label: "Customer Type" },
  { key: "group", label: "Sales Order Group" },
  { key: "sisters", label: "Sister services" },
];

function HeaderCell({ col, sort, onSort }) {
  if (!onSort) return <div className="whitespace-nowrap">{col.label}</div>;
  const active = sort?.key === col.key;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "⬍";
  const state = active ? (sort.dir === "asc" ? "ascending" : "descending") : "not sorted";
  return (
    <div className="whitespace-nowrap">
      <button
        type="button"
        onClick={function () {
          onSort(col.key);
        }}
        aria-label={`Sort by ${col.label} (${state})`}
        className="m-0 inline-flex cursor-pointer items-center gap-1 border-0 p-0 text-left text-white uppercase"
        style={{
          font: "inherit",
          letterSpacing: "inherit",
          background: active ? "rgba(255,255,255,0.18)" : "transparent",
          borderRadius: 4,
          padding: active ? "2px 5px" : 0,
          opacity: active ? 1 : 0.9,
        }}
      >
        {col.label}
        <span aria-hidden="true">{arrow}</span>
      </button>
    </div>
  );
}

// db-205: `sisterIndex` (Map shelter id -> { sisters }) adds a "Sister services" column
// of short-code chips (hover = name, NOT clickable). Optional — absent/empty = "—".
export function UserAdminShelterList({
  filteredShelters,
  onOpenShelter,
  pendingShelterIds,
  sisterIndex,
  sort,
  onSort,
}) {
  const cols = "130px 1fr 70px 130px 170px 170px 80px";
  return (
    <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
      <div
        className="grid gap-0 bg-zephyr-teal-dark text-white py-[10px] px-[18px] text-[11px] font-bold uppercase tracking-[0.5px]"
        style={{ gridTemplateColumns: cols }}
      >
        {COLUMNS.map(function (col) {
          return <HeaderCell key={col.key} col={col} sort={sort} onSort={onSort} />;
        })}
        <div>Actions</div>
      </div>
      {filteredShelters.map(function (s, idx) {
        return (
          <div
            key={s.id}
            style={{
              borderBottom: idx < filteredShelters.length - 1 ? "1px solid #CFE0DE" : "none",
              background: idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
            }}
          >
            <div
              className="grid gap-0 py-[10px] px-[18px] items-center"
              style={{ gridTemplateColumns: cols }}
            >
              <div>
                <button
                  type="button"
                  onClick={function () {
                    onOpenShelter(s);
                  }}
                  className="bg-transparent border-0 p-0 font-bold text-[13px] text-zephyr-teal-dark underline cursor-pointer text-left"
                  style={{ fontFamily: "inherit" }}
                >
                  {s.shelter_code || "—"}
                </button>
              </div>
              <div>
                <span className="font-semibold text-[13px] text-zephyr-text">
                  {s.shelter_name || "—"}
                </span>
                {s.internal_notes && (
                  <span title={s.internal_notes} className="ml-[6px] text-[11px] cursor-help">
                    📝
                  </span>
                )}
                {s.deactivated && (
                  <span className="ml-[6px] text-[10px] font-bold text-white bg-zephyr-red rounded-[3px] py-[1px] px-[6px]">
                    DEACTIVATED
                  </span>
                )}
                {pendingShelterIds?.has(s.id) && (
                  <span
                    title="Has a delivery address awaiting Zephyr verification"
                    className="ml-[6px] text-[10px] font-bold text-white rounded-[3px] py-[1px] px-[6px]"
                    style={{ background: "#F57C00" }}
                  >
                    ADDRESS PENDING
                  </span>
                )}
              </div>
              <div className="text-[12px] text-zephyr-muted">{s.state || "—"}</div>
              <div className="text-[12px] text-zephyr-muted">{s.customer_type || "—"}</div>
              <div className="text-[12px] text-zephyr-muted">{s.sales_group || "—"}</div>
              <div className="flex flex-wrap gap-[4px] text-[12px] text-zephyr-muted">
                {sisterIndex?.get(s.id)?.sisters.length
                  ? sisterIndex.get(s.id).sisters.map((sis) => (
                      <span
                        key={sis.id}
                        title={sis.name}
                        className="inline-flex items-center bg-white border border-[#80CBC4] text-zephyr-teal-dark rounded-full py-[1px] px-[8px] text-[11px] font-semibold cursor-default"
                      >
                        {sis.code}
                      </span>
                    ))
                  : "—"}
              </div>
              <div className="flex gap-[5px]">
                <button
                  type="button"
                  onClick={function () {
                    onOpenShelter(s);
                  }}
                  className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer"
                  style={{
                    border: "1px solid #CFE0DE",
                    color: "#1A2E2B",
                    fontFamily: "inherit",
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        );
      })}
      {filteredShelters.length === 0 && (
        <div className="p-10 text-center text-zephyr-muted text-[13px]">No DFV Services found.</div>
      )}
    </div>
  );
}
